/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { string, shape, arrayOf } from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import loadable from '@loadable/component';

import RecentlyViewedRecommendation from '../PersonalizedRecommendations/RecentlyViewedRecommendation';
import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import findContentByUID from '../../../../gql/queries/findContentByUID';

const LoadableTrendingProductsABContainer = loadable(() => import(/* webpackChunkName: "TrendingProductsABContainer" */ '../../GraphqlCommonComponents/TrendingProducts/TrendingProductsABContainer'));
const LoadableTrendingShopByCategory = loadable(() => import(/* webpackChunkName: "TrendingShopByCategory" */ '../../GraphqlCommonComponents/TrendingProducts/TrendingShopByCategory'));

const PersonalizedRecommendations = ({ data }) => {
    const brand = useSelector(getBrandName);
    const { recommendation_type: recommendationType, layout_display_type: layoutDisplayType, reference } = data;

    if (!reference?.[0]?.uid || !reference?.[0]?._content_type_uid) {
        return null;
    }

    const findContentOptions = {
        brand,
        contentType: reference[0]._content_type_uid,
        uid: reference[0].uid,
        includeRefs: ['personalized_product_card_reference'],
    };

    const CONTENT_DETAILS = findContentByUID(findContentOptions);
    const { data: contentData, loading, error } = useQuery(CONTENT_DETAILS);

    if (loading) return <></>;

    if (error) {
        mbpLogger.logError({
            function: 'PersonalizedRecommendations.js',
            message: `Failed to fetch content data for recommendation ${recommendationType} : content type ${reference[0]._content_type_uid}, on brand ${brand}`,
            appName: process.env.npm_package_name,
            module: 'PersonalizedRecommendations',
            jsError: error,
        });
        return null;
    }

    if (!contentData?.findContentByUID?.content?.entry?.uid) return <></>;

    switch (recommendationType) {
        case 'recently_viewed':
            return <RecentlyViewedRecommendation layoutDisplayType={layoutDisplayType} content={contentData.findContentByUID.content.entry} />;

        case 'shop_by_category':
            return <LoadableTrendingShopByCategory layoutDisplayType={layoutDisplayType} content={contentData.findContentByUID.content.entry} />;

        case 'trending_products':
            return <LoadableTrendingProductsABContainer layoutDisplayType={layoutDisplayType} content={contentData.findContentByUID.content.entry} />;

        default:
            return null;
    }
};

PersonalizedRecommendations.propTypes = {
    data: shape({
        recommendation_type: string.isRequired,
        layout_display_type: string.isRequired,
        reference: arrayOf(shape({
            uid: string.isRequired,
            _content_type_uid: string.isRequired,
        })).isRequired,
    }).isRequired,
};

export default PersonalizedRecommendations;
